import "core-js/es/map";
// needed for Yup to work with old browsers
import "core-js/es/promise";
import "core-js/es/set";
import { Formik, FormikContextType, FormikErrors, FormikHelpers, FormikProps, FormikValues } from "formik";
import { isEmpty, isEqual, isFunction } from "lodash";
import React, { createRef } from "react";
import { Prompt } from "react-router-dom";
import { Alert } from "../";
import BrushfireField from "./BrushfireField";
import Description from "./Description";
import Field from "./Field";
import Input from "./Input";
import Summary from "./Summary";

export type OnSubmit<T = Record<string, unknown>> =
	| ((values: any) => void)
	| ((values: any, formikActions: FormikHelpers<FormikValues>) => void)
	| ((values: any, formikActions: FormikHelpers<FormikValues>, setGlobalError: (error: string) => void) => void);
export interface FormProps {
	children?: React.ReactNode | ((formikContext: FormContext) => React.ReactNode);
	handleBlur?: (e: any) => void;
	initialValues: { [key: string]: any };
	isInitialValid?: boolean;
	onSubmit: OnSubmit;
	validate?: (values: any, props?: any) => void | Record<string, unknown> | Promise<FormikErrors<any>>;
	validateOnBlur?: boolean;
	validateOnChange?: boolean;
	validateOnMount?: boolean;
	validationSchema?: any;
	enableReinitialize?: boolean;
	autoComplete?: string;
	useUnsavedPrompt?: boolean;
	formikBagProps?: { [key: string]: any };
}

export interface FormState {
	showSummary: boolean;
	globalError?: string;
	showBuffer: boolean;
	assessBuffer: boolean;
}

export interface FormContext<T = any> {
	formikContext: FormikContextType<T>;
	extraData: FormState;
	setGlobalError: (globalError: string) => void;
	formikRef: React.RefObject<FormikProps<FormikValues>>;
}

class Form extends React.Component<FormProps, FormState> {
	static BrushfireField = BrushfireField;
	static Description = Description;
	static Field = Field;
	static Input = Input;
	static Summary = Summary;

	myFormikRef = createRef<FormikProps<FormikValues>>();
	myFormRef = createRef<HTMLFormElement>();

	state = {
		showSummary: false,
		globalError: undefined,
		showBuffer: false,
		assessBuffer: true,
	};

	setGlobalError = (globalError: string) => {
		this.setState({ globalError });
	};

	localSubmit = (values: any, formikHelpers: FormikHelpers<FormikValues>) => {
		const { onSubmit } = this.props;
		this.setState({ globalError: undefined });
		onSubmit(values, formikHelpers, this.setGlobalError);
	};

	assessIfBufferShouldShow = () => {
		this.setState({ assessBuffer: false });
		const openMenus = document.getElementsByClassName("react-select__control--is-focused");
		if (openMenus.length === 1) {
			const dif =
				(this.myFormRef.current?.getBoundingClientRect().bottom ?? 0) -
				openMenus[0].getBoundingClientRect().top;
			if (dif < 350) {
				this.setState({ showBuffer: true });
			}
		}
	};

	render() {
		const {
			children,
			handleBlur,
			initialValues,
			isInitialValid = false,
			validate,
			validateOnBlur = true,
			validateOnChange = true,
			validateOnMount = false,
			validationSchema,
			useUnsavedPrompt = true,
			formikBagProps,
			...otherProps
		} = this.props;
		const { showSummary, globalError, showBuffer, assessBuffer } = this.state;
		return (
			<Formik
				innerRef={this.myFormikRef}
				initialValues={initialValues}
				isInitialValid={isInitialValid || (validationSchema && validationSchema.isValid(initialValues))}
				onSubmit={this.localSubmit}
				validate={validate}
				validateOnBlur={validateOnBlur}
				validateOnChange={validateOnChange}
				validateOnMount={validateOnMount}
				validationSchema={validationSchema}
				formikBagProps={{ ...formikBagProps, setGlobalError: this.setGlobalError }}
			>
				{(formikContext: FormikContextType<any>) => {
					// only show the summary if we tried to submit and there are errors to display
					if (formikContext.isSubmitting && !showSummary && !isEmpty(formikContext.errors)) {
						this.setState({ showSummary: true });
					}
					if (!formikContext.isSubmitting && isEmpty(formikContext.errors) && showSummary) {
						this.setState({ showSummary: false });
					}
					if (formikBagProps && !isEqual(formikBagProps, formikContext.status?.bag)) {
						const newStatus = Object.assign({}, formikContext.status ?? {}, { bag: formikBagProps });
						formikContext.setStatus(newStatus);
					}
					if (formikContext.status?.menuOpen && this.state.assessBuffer) {
						this.assessIfBufferShouldShow();
					}
					if (formikContext.status?.menuOpen === false && !this.state.assessBuffer) {
						this.setState({ assessBuffer: true });
					}
					const extraData: FormState = {
						showSummary,
						showBuffer,
						assessBuffer,
					};

					formikContext.validationSchema = validationSchema;
					formikContext.status
						? (formikContext.status.setGlobalError = this.setGlobalError)
						: (formikContext.status = { setGlobalError: this.setGlobalError });
					formikContext.status
						? (formikContext.status.formikRef = this.myFormikRef)
						: (formikContext.status = { formikRef: this.myFormikRef });
					return (
						<>
							{useUnsavedPrompt && (
								<Prompt
									when={!formikContext.isSubmitting && formikContext.dirty}
									message={(location, action) => {
										return window.location.pathname.endsWith("/details") ||
											location.pathname.endsWith("/details")
											? true
											: "Your changes on this page will be lost";
									}}
								/>
							)}

							{globalError && (
								<div id="form-error" tabIndex={0}>
									<Alert purpose="danger">{globalError}</Alert>
								</div>
							)}

							<form
								ref={this.myFormRef}
								{...otherProps}
								noValidate={true}
								onSubmit={formikContext.handleSubmit}
							>
								{// allows us to give formikContext to the parent component if they use render props
								isFunction(children)
									? children({
											formikContext,
											extraData,
											setGlobalError: this.setGlobalError,
											formikRef: this.myFormikRef,
									  })
									: children}
								{showBuffer && <div className="react-select-menu-buffer"></div>}
							</form>
						</>
					);
				}}
			</Formik>
		);
	}
}

export default Form;
